/**
 * SEO validation utilities for development mode
 * These functions help ensure SEO metadata meets best practices
 */

export interface SEOValidationResult {
  valid: boolean;
  warnings: string[];
  errors: string[];
}

/**
 * Validates title length and format
 * Best practices: 50-60 characters, includes brand name
 */
export const validateTitle = (title: string): SEOValidationResult => {
  const warnings: string[] = [];
  const errors: string[] = [];

  if (!title) {
    errors.push('Title is required');
  } else {
    if (title.length < 30) {
      warnings.push(`Title is too short (${title.length} chars). Recommended: 50-60 characters.`);
    }
    if (title.length > 80) {
      warnings.push(`Title is too long (${title.length} chars). Recommended: 50-60 characters.`);
    }
    if (!title.includes('HTL MarketCompass')) {
      warnings.push('Title should include brand name "HTL MarketCompass"');
    }
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  };
};

/**
 * Validates meta description length
 * Best practices: 150-160 characters
 */
export const validateDescription = (description: string): SEOValidationResult => {
  const warnings: string[] = [];
  const errors: string[] = [];

  if (!description) {
    errors.push('Description is required');
  } else {
    if (description.length < 120) {
      warnings.push(
        `Description is too short (${description.length} chars). Recommended: 150-160 characters.`
      );
    }
    if (description.length > 200) {
      warnings.push(
        `Description is too long (${description.length} chars). Recommended: 150-160 characters.`
      );
    }
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  };
};

/**
 * Validates canonical URL format
 * Should be absolute URL, no query params, no hash, no trailing slash
 */
export const validateCanonicalUrl = (url: string): SEOValidationResult => {
  const warnings: string[] = [];
  const errors: string[] = [];

  if (!url) {
    errors.push('Canonical URL is required');
    return { valid: false, warnings, errors };
  }

  try {
    const urlObj = new URL(url);

    // Check for query parameters
    if (urlObj.search) {
      warnings.push('Canonical URL should not contain query parameters');
    }

    // Check for hash fragments
    if (urlObj.hash) {
      warnings.push('Canonical URL should not contain hash fragments');
    }

    // Check for trailing slash (except root)
    if (urlObj.pathname.length > 1 && urlObj.pathname.endsWith('/')) {
      warnings.push('Canonical URL should not have a trailing slash');
    }

    // Check protocol
    if (urlObj.protocol !== 'https:') {
      warnings.push('Canonical URL should use HTTPS protocol');
    }
  } catch (error) {
    errors.push('Canonical URL is not a valid URL');
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  };
};

/**
 * Validates image URL and format
 * Best practices: absolute URL, proper dimensions referenced
 */
export const validateImage = (imageUrl: string): SEOValidationResult => {
  const warnings: string[] = [];
  const errors: string[] = [];

  if (!imageUrl) {
    warnings.push('Image URL is recommended for better social sharing');
    return { valid: true, warnings, errors };
  }

  try {
    const url = new URL(imageUrl);
    if (url.protocol !== 'https:') {
      warnings.push('Image URL should use HTTPS protocol');
    }
  } catch (error) {
    errors.push('Image URL is not a valid absolute URL');
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  };
};

/**
 * Validates structured data schema
 * Checks for required properties based on schema type
 */
export const validateStructuredData = (schema: any): SEOValidationResult => {
  const warnings: string[] = [];
  const errors: string[] = [];

  if (!schema) {
    return { valid: true, warnings, errors };
  }

  // Check for @context
  if (!schema['@context']) {
    errors.push('Structured data must include @context property');
  }

  // Check for @type or @graph (both are valid at top level)
  // @graph is used when bundling multiple schemas together
  if (!schema['@type'] && !schema['@graph']) {
    errors.push('Structured data must include @type or @graph property');
  }

  // If using @graph, validate each schema in the array
  if (schema['@graph'] && Array.isArray(schema['@graph'])) {
    schema['@graph'].forEach((graphSchema: any, index: number) => {
      if (!graphSchema['@type']) {
        errors.push(`Schema in @graph[${index}] must include @type property`);
      }
    });
    // Return early for @graph schemas - individual validation happens above
    return { valid: errors.length === 0, warnings, errors };
  }

  // Validate common schema types
  const schemaType = schema['@type'];
  switch (schemaType) {
    case 'Product':
      if (!schema.name) warnings.push('Product schema should include name');
      if (!schema.description) warnings.push('Product schema should include description');
      if (!schema.offers) warnings.push('Product schema should include offers');
      break;
    case 'Article':
      if (!schema.headline) warnings.push('Article schema should include headline');
      if (!schema.author) warnings.push('Article schema should include author');
      if (!schema.datePublished) warnings.push('Article schema should include datePublished');
      break;
    case 'BreadcrumbList':
      if (!schema.itemListElement) {
        errors.push('BreadcrumbList schema must include itemListElement');
      }
      break;
    case 'FAQPage':
      if (!schema.mainEntity) {
        errors.push('FAQPage schema must include mainEntity');
      }
      break;
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  };
};

/**
 * Comprehensive SEO validation for a page
 */
export const validatePageSEO = (seoProps: {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  structuredData?: any;
  schemas?: any[];
}): SEOValidationResult => {
  const allWarnings: string[] = [];
  const allErrors: string[] = [];

  // Validate title
  const titleResult = validateTitle(seoProps.title);
  allWarnings.push(...titleResult.warnings);
  allErrors.push(...titleResult.errors);

  // Validate description
  const descResult = validateDescription(seoProps.description);
  allWarnings.push(...descResult.warnings);
  allErrors.push(...descResult.errors);

  // Validate canonical if provided
  if (seoProps.canonical) {
    const canonicalResult = validateCanonicalUrl(seoProps.canonical);
    allWarnings.push(...canonicalResult.warnings);
    allErrors.push(...canonicalResult.errors);
  }

  // Validate image if provided
  if (seoProps.image) {
    const imageResult = validateImage(seoProps.image);
    allWarnings.push(...imageResult.warnings);
    allErrors.push(...imageResult.errors);
  }

  // Validate structured data
  if (seoProps.structuredData) {
    const structuredDataResult = validateStructuredData(seoProps.structuredData);
    allWarnings.push(...structuredDataResult.warnings);
    allErrors.push(...structuredDataResult.errors);
  }

  // Validate multiple schemas
  if (seoProps.schemas) {
    seoProps.schemas.forEach((schema, index) => {
      const schemaResult = validateStructuredData(schema);
      schemaResult.warnings.forEach(w => allWarnings.push(`Schema ${index}: ${w}`));
      schemaResult.errors.forEach(e => allErrors.push(`Schema ${index}: ${e}`));
    });
  }

  return {
    valid: allErrors.length === 0,
    warnings: allWarnings,
    errors: allErrors,
  };
};
